import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useRols, type RolDto, type UsuariRolDto } from '@/api/administracio';
import { useAssignaAmbit, useTreuAssignacions } from '@/api/ajustosPersones';
import { Desplegable } from '@/components/Desplegable';
import { Icona } from '@/components/Icona';
import { Buit } from '@/pages/operacio/comu';
import { Confirmacio } from '../marc/Calaix';
import { CercaAmbit } from '../marc/CercaAmbit';
import { ErrorDesa } from '../comu';

interface GrupRol {
  rolId: number;
  rol?: RolDto;
  nom: string;
  assignacions: UsuariRolDto[];
}

/**
 * Pestanya Accessos: un rol per targeta, amb els llocs on s'aplica com a etiquetes.
 * S'afegeix un lloc escrivint-ne el nom (departament, àrea o servei) i es treu amb la ×.
 */
export function AccessosUsuari({
  usuariId,
  nomPersona,
  rols: assignacions,
  editable,
}: {
  usuariId: number;
  nomPersona: string;
  rols: UsuariRolDto[];
  editable: boolean;
}) {
  const { t, i18n } = useTranslation('administracio');
  const tc = useTranslation('comu').t;
  const es = i18n.language === 'es';
  const cataleg = useRols();
  const assigna = useAssignaAmbit(usuariId);
  const treu = useTreuAssignacions(usuariId);
  const [nous, setNous] = useState<number[]>([]);
  const [treure, setTreure] = useState<GrupRol | null>(null);

  const nomRol = (r: RolDto | undefined, a?: UsuariRolDto) =>
    (r ? (es ? r.nomEs : r.nomCa) : es ? a?.rolNomEs : a?.rolNomCa) ?? a?.rolCodi ?? '';

  const grups: GrupRol[] = [];
  for (const a of assignacions) {
    let g = grups.find((x) => x.rolId === a.rolId);
    if (!g) {
      const rol = cataleg.data?.find((r) => r.id === a.rolId);
      g = { rolId: a.rolId, rol, nom: nomRol(rol, a), assignacions: [] };
      grups.push(g);
    }
    g.assignacions.push(a);
  }
  for (const id of nous) {
    if (grups.some((g) => g.rolId === id)) continue;
    const rol = cataleg.data?.find((r) => r.id === id);
    grups.push({ rolId: id, rol, nom: nomRol(rol), assignacions: [] });
  }
  const disponibles = (cataleg.data ?? []).filter(
    (r) => r.id != null && !grups.some((g) => g.rolId === r.id),
  );

  const afegeix = (g: GrupRol, ambit: UsuariRolDto['ambit'], ambitId: number | null) =>
    assigna.mutate({ usuariId, rolId: g.rolId, ambit, ambitId });

  const on = (g: GrupRol) =>
    g.assignacions
      .map((a) =>
        a.ambit === 'GLOBAL'
          ? t('persones.accessos.totaOrganitzacio')
          : (a.ambitNom ?? `#${a.ambitId}`),
      )
      .join(', ') || t('persones.accessos.enCapLloc');

  return (
    <div className="stack accessos" data-accessos={usuariId}>
      <p className="t-footnote dim" style={{ margin: 0 }}>
        <Trans t={t} i18nKey="persones.accessos.ajuda" components={{ b: <strong /> }} />
      </p>
      {grups.length === 0 ? (
        <Buit icona="person-key">{t('persones.accessos.senseRols')}</Buit>
      ) : null}
      {grups.map((g) => {
        const global = g.assignacions.find((a) => a.ambit === 'GLOBAL');
        const parcials = g.assignacions.filter((a) => a.ambit !== 'GLOBAL');
        return (
          <section
            key={g.rolId}
            className="panel rol-targeta"
            aria-label={g.nom}
            data-rol={g.rolId}
          >
            <div className="panel__head">
              <h3>{g.nom}</h3>
              {g.rol?.descripcio ? (
                <span className="t-caption dim truncate">{g.rol.descripcio}</span>
              ) : null}
              <div className="spacer" />
              <Link className="btn btn--ghost btn--xs" to={`/admin/rols?rol=${g.rolId}`}>
                {t('persones.accessos.quePotFer')}
              </Link>
              {editable && g.assignacions.length > 0 ? (
                <button
                  type="button"
                  className="btn btn--danger btn--xs"
                  onClick={() => setTreure(g)}
                >
                  {t('persones.accessos.treuRol')}
                </button>
              ) : null}
            </div>
            <div className="panel__body stack">
              <div className="rol-targeta__ambits">
                {global ? (
                  <span className="xip-ambit xip-ambit--global">
                    {t('persones.accessos.totaOrganitzacio')}
                    {editable && global.id != null ? (
                      <button
                        type="button"
                        aria-label={t('persones.accessos.treuAmbit', {
                          nom: t('persones.accessos.totaOrganitzacio'),
                        })}
                        disabled={treu.isPending}
                        onClick={() => treu.mutate([global.id as number])}
                      >
                        <Icona nom="xmark" />
                      </button>
                    ) : null}
                  </span>
                ) : null}
                {parcials.map((a) => {
                  const nom = a.ambitNom ?? `#${a.ambitId}`;
                  return (
                    <span
                      key={a.id ?? `${a.ambit}-${a.ambitId}`}
                      className="xip-ambit"
                      title={tc(`ambit.${a.ambit}`)}
                    >
                      <span
                        className={`node-punt node-punt--${a.ambit.toLowerCase()}`}
                        aria-hidden="true"
                      />
                      {nom}
                      {editable && a.id != null ? (
                        <button
                          type="button"
                          aria-label={t('persones.accessos.treuAmbit', { nom })}
                          disabled={treu.isPending}
                          onClick={() => treu.mutate([a.id as number])}
                        >
                          <Icona nom="xmark" />
                        </button>
                      ) : null}
                    </span>
                  );
                })}
                {g.assignacions.length === 0 ? (
                  <span className="t-footnote rol-targeta__buit">
                    {t('persones.accessos.encaraEnlloc')}
                  </span>
                ) : null}
              </div>
              {editable && !global ? (
                <div className="rol-targeta__afegeix">
                  <CercaAmbit
                    aria-label={t('persones.accessos.afegeixAmbitA', { rol: g.nom })}
                    placeholder={t('persones.accessos.afegeixAmbit')}
                    autoFocus={g.assignacions.length === 0}
                    exclou={(n) => parcials.some((a) => a.ambit === n.tipus && a.ambitId === n.id)}
                    onTria={(n) => afegeix(g, n.tipus, n.id)}
                  />
                  <button
                    type="button"
                    className="btn btn--ghost btn--xs"
                    disabled={assigna.isPending}
                    onClick={() => afegeix(g, 'GLOBAL', null)}
                  >
                    {t('persones.accessos.donaGlobal')}
                  </button>
                </div>
              ) : null}
            </div>
          </section>
        );
      })}
      {editable && disponibles.length > 0 ? (
        <div className="accessos__nou">
          <Desplegable
            aria-label={t('persones.accessos.afegeixRol')}
            placeholder={t('persones.accessos.afegeixRol')}
            valor=""
            opcions={disponibles.map((r) => ({
              valor: r.id as number,
              etiqueta: nomRol(r),
              detall: r.descripcio ?? undefined,
            }))}
            onCanvia={(id) => setNous((n) => [...n, Number(id)])}
          />
        </div>
      ) : null}
      <ErrorDesa error={assigna.error ?? treu.error} />
      {treure ? (
        <Confirmacio
          titol={t('persones.accessos.treuRolTitol', { rol: treure.nom })}
          textAccio={t('persones.accessos.treuRol')}
          perill
          ocupat={treu.isPending}
          onTanca={() => setTreure(null)}
          onConfirma={() => {
            const ids = treure.assignacions.map((a) => a.id).filter((x): x is number => x != null);
            treu.mutate(ids, {
              onSettled: () => {
                setNous((n) => n.filter((x) => x !== treure.rolId));
                setTreure(null);
              },
            });
          }}
        >
          <p>
            {t('persones.accessos.treuRolText', {
              nom: nomPersona,
              rol: treure.nom.toLowerCase(),
              on: on(treure),
            })}
          </p>
        </Confirmacio>
      ) : null}
    </div>
  );
}
